import React, { useState } from 'react';
import proptypes from 'prop-types';
import classnames from 'classnames';
import {
  Button,
  FormLabel,
  IconButton,
  Input,
  Typography
} from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import { useConfigService } from '@thd-nucleus/experience-context';
import { ThumbsUpThumbsDownWithPopover } from '@thd-olt-component-react/user-feedback';
import { Close } from '@one-thd/sui-icons';
import { useQuestionBot } from '../../hooks/useQuestionBot';
import { getSessionId, createActionId } from '../../utils/session-utils';
import { Spinner } from '../../assets/Spinner';
import {
  FEEDBACK_ANALYTICS_QNA_PRIMARY_CATEGORY,
  FEEDBACK_ANALYTICS_QNA_EVENT_NAME_PREFIX,
  FALLBACK_SESSION_ID,
  FEEDBACK_CLICK_COMMENT_QUESTION_ORIGIN_QNA,
} from '../MagicApronAssistant/constants';
import { BotResponse } from './BotResponse';

const { bool, string } = proptypes;

const USER_QUESTION_CHARACTER_LIMIT = 255;
const keyPressMap = { Enter: false, Shift: false };

export const BotPrompt = ({ itemId, force }) => {
  const [userQuestion, setUserQuestion] = useState('');
  const [submittedQuestion, setSubmittedQuestion] = useState('');
  const { error, loading, response } = useQuestionBot(submittedQuestion, itemId);

  const magicApronBotEnabled = useConfigService('fs-prop:magic-apron-qa-bot-enabled');

  if (!magicApronBotEnabled && !force) {
    return null;
  }

  const onUserQuestionSubmit = () => {
    const currentSessionID = getSessionId();
    window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-bot.submit-click', {
      sessionID: currentSessionID,
      actionID: createActionId(currentSessionID),
      question: `${FEEDBACK_CLICK_COMMENT_QUESTION_ORIGIN_QNA}:${userQuestion}`,
    });
    setSubmittedQuestion(userQuestion);
  };
  const onUserQuestionChange = (event) => {
    if (!(keyPressMap.Enter)) {
      setUserQuestion(event.target.value);
    }
  };

  const onUserQuestionKeyPress = (event) => {
    // Keeps track of all currently pressed keys
    keyPressMap[event.key] = event.type === 'keydown';
    if (keyPressMap.Enter) {
      onUserQuestionSubmit();
    }
  };

  const clearPrompt = () => {
    setUserQuestion('');
    setSubmittedQuestion('');
  };

  const handleFeedbackData = () => {
    return {
      questionAndAnswer: {
        question: userQuestion,
        answer: response
      },
      reviewSummary: ''
    };
  };

  if (!userQuestion && submittedQuestion) {
    clearPrompt();
  }

  const feedbackAnalytics = {
    primaryCategory: FEEDBACK_ANALYTICS_QNA_PRIMARY_CATEGORY,
    eventName: FEEDBACK_ANALYTICS_QNA_EVENT_NAME_PREFIX,
    sessionID: getSessionId(),
    question: `${FEEDBACK_CLICK_COMMENT_QUESTION_ORIGIN_QNA}:${userQuestion}`,
    fallbackSessionID: FALLBACK_SESSION_ID,
    feedbackClickCommentOrigin: FEEDBACK_CLICK_COMMENT_QUESTION_ORIGIN_QNA
  };

  const containerClasses = classnames('sui-p-4 sm:sui-p-8 sui-flex sui-flex-col sm:sui-flex-row '
    + 'sui-gap-2 sm:sui-gap-3 sui-bg-primary sui-w-full sui-mb-4 sui-border-1 sui-border-primary sui-border-solid');

  return (
    <div
      data-component="magic-apron-bot-prompt"
      className={containerClasses}
    >
      <div className="sui-flex sui-items-center">
        <div className="sui-mr-4">
          <div className="sui-flex sui-items-center sui-justify-center sui-w-14 sui-h-14">
            <Spinner spinning={!!loading} />
          </div>
        </div>
        <div className="sui-flex sui-flex-col sui-min-w-32">
          <FormLabel htmlFor="qa-bot-input">
            <Typography variant="h3">Ask about this product</Typography>
          </FormLabel>
          <Typography variant="body-base">Get an answer now with AI</Typography>
        </div>
      </div>
      <div className="sui-flex sui-flex-col sui-flex-grow sui-p-4 sui-bg-subtle">
        <div className="sui-flex sui-flex-col sm:sui-flex-row">
          <div className="sui-mb-2 sm:sui-grow sm:sui-mr-4">
            <Input
              id="qa-bot-input"
              value={userQuestion}
              onChange={onUserQuestionChange}
              onKeyDown={onUserQuestionKeyPress}
              onKeyUp={onUserQuestionKeyPress}
              fullWidth
              multiline
              placeholder="Type a question"
              inputAttributes={{
                maxLength: USER_QUESTION_CHARACTER_LIMIT,
                resize: 'none',
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: 'false'
              }}
              endAdornment={!!userQuestion.length && (
                <IconButton
                  icon={Close}
                  size="small"
                  onClick={clearPrompt}
                  aria-label="Clear question"
                />
              )}
            />
          </div>
          <div className="sui-mb-4">
            <Button
              variant="secondary"
              onClick={onUserQuestionSubmit}
              id="magic_apron_qa_find_it_for_me"
              disabled={loading}
            >
              Get an Answer
            </Button>
          </div>
        </div>
        {/* eslint-disable-next-line @harmony/stencil/no-tailwind-disable */}
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <div className="sui-flex sui-flex-col sui-mb-2 sui-max-w-[800px]">
          <BotResponse loading={loading} error={error} response={response} />
        </div>
        {/* eslint-disable-next-line @harmony/stencil/no-tailwind-disable */}
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <div className="sui-max-w-[800px]">
          <Typography variant="body-xs" color="subtle" height="tight">
            {/* eslint-disable-next-line max-len */}
            AI-generated from the text of manufacturer documentation. To verify or get additional information, please contact The Home Depot customer service.
          </Typography>
        </div>
        {response && !loading && (
          <div className="sui-mt-4">
            <ThumbsUpThumbsDownWithPopover
              anchorKey={itemId}
              anchorType="PIP"
              customerInsights="QABot"
              handleFeedbackData={handleFeedbackData}
              feedbackAnalytics={feedbackAnalytics}
            />
          </div>
        )}
      </div>
    </div>
  );
};

BotPrompt.displayName = 'BotPrompt';
BotPrompt.propTypes = {
  itemId: string,
  force: bool
};

BotPrompt.defaultProps = {
  itemId: '',
  force: false
};

BotPrompt.dataModel = extend(
  ThumbsUpThumbsDownWithPopover
);
